import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FlaskConical, Play, Trash2, RefreshCw, X, Eye } from "lucide-react";
import api from "@/lib/api";

const badge = (v) => {
  const map = { PASS: "bg-emerald-50 text-emerald-700 border-emerald-200", FAIL: "bg-red-50 text-red-700 border-red-200",
    PARTIAL: "bg-amber-50 text-amber-700 border-amber-200", running: "bg-cobalt/10 text-cobalt border-cobalt/30" };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${map[v] || "bg-slate-50 text-slate-500 border-slate-200"}`}>{v || "NOT RUN"}</span>;
};

function TestDriveControls() {
  const [cfg, setCfg] = useState(null);
  const [fb, setFb] = useState(null);
  const [sessions, setSessions] = useState(null);
  const load = () => {
    api.get("/admin/test-drive/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/admin/test-drive/feedback").then(({ data }) => setFb(data)).catch(() => {});
    api.get("/admin/test-drive/sessions").then(({ data }) => setSessions(data)).catch(() => {});
  };
  useEffect(load, []);
  const save = async (updates) => {
    const { data } = await api.put("/admin/test-drive/config", updates);
    setCfg(data); toast.success("Test Drive settings updated");
  };
  if (!cfg) return null;
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="td-controls">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading font-semibold text-navy">Test Drive Controls <span className="text-xs font-normal text-slate-400">— public /test-drive experience</span></h3>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => save({ paused: !cfg.paused })} data-testid="td-ctrl-pause"
            className={`px-4 py-2 rounded-full text-xs font-semibold text-white ${cfg.paused ? "bg-emerald-600" : "bg-orange-600"}`}>
            {cfg.paused ? "Resume testing" : "Pause testing"}
          </button>
          <button onClick={async () => { const { data } = await api.post("/admin/test-drive/force-logout"); toast.success(`${data.sessions_revoked} demo sessions revoked`); load(); }}
            data-testid="td-ctrl-logout" className="px-4 py-2 rounded-full text-xs font-semibold bg-red-600 text-white">Force logout all testers</button>
        </div>
      </div>
      <div className="mt-4 grid sm:grid-cols-4 gap-3 text-xs">
        <div><label className="font-semibold text-slate-500 uppercase text-[10px]">Invite expiration</label>
          <input type="date" defaultValue={cfg.expires_at?.slice(0, 10)} data-testid="td-ctrl-expires"
            onBlur={(e) => e.target.value && save({ expires_at: `${e.target.value}T23:59:59+00:00` })}
            className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200" /></div>
        <div><label className="font-semibold text-slate-500 uppercase text-[10px]">Max sessions</label>
          <input type="number" defaultValue={cfg.max_sessions} data-testid="td-ctrl-max"
            onBlur={(e) => save({ max_sessions: parseInt(e.target.value || "500") })}
            className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200" /></div>
        <div><label className="font-semibold text-slate-500 uppercase text-[10px]">Admin demo access code</label>
          <input defaultValue={cfg.admin_code} data-testid="td-ctrl-code"
            onBlur={(e) => e.target.value && save({ admin_code: e.target.value })}
            className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200" /></div>
        <div className="bg-slate-50 rounded-xl p-3">
          <div className="font-heading text-xl font-extrabold text-navy">{sessions?.count ?? 0}</div>
          <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Tester sessions</div>
        </div>
      </div>
      <div className="mt-5">
        <h4 className="font-heading font-semibold text-navy text-sm">Tester Feedback Inbox ({fb?.count ?? 0})</h4>
        <div className="mt-2 space-y-2 max-h-72 overflow-y-auto" data-testid="td-feedback-inbox">
          {(fb?.feedback || []).map((r) => (
            <div key={r.feedback_id} className={`rounded-xl border px-3 py-2 text-xs ${r.kind === "bug" ? "border-red-100 bg-red-50/40" : "border-cobalt/20 bg-cobalt/5"}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-navy uppercase text-[10px]">{r.kind} · {r.severity || "—"} · {r.role || "unknown role"}</span>
                <span className="text-slate-400">{r.created_at?.slice(0, 16).replace("T", " ")}</span>
              </div>
              <div className="text-slate-700 mt-1">{r.description}</div>
              {r.steps && <div className="text-slate-400 mt-0.5">Steps: {r.steps}</div>}
              <div className="text-slate-400 mt-0.5 break-all">{r.page} · {r.device} · session {r.session_id || "n/a"}</div>
            </div>
          ))}
          {(!fb || fb.count === 0) && <p className="text-xs text-slate-400 py-3">No feedback yet — share {window.location.origin}/test-drive with testers.</p>}
        </div>
      </div>
    </div>
  );
}

export default function TestCenter() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [log, setLog] = useState(null);

  const load = () => api.get("/admin/test-center/status").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, []);

  const act = async (label, fn) => {
    setBusy(label);
    try { const { data } = await fn(); toast.success(data.note || `${label} started`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    finally { setBusy(""); }
  };

  const openLog = async (runId) => {
    const { data } = await api.get(`/admin/test-center/runs/${runId}`);
    setLog(data);
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="test-center">
      <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm font-semibold text-amber-700" data-testid="test-center-banner">
        {data.banner}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-extrabold text-navy flex items-center gap-2"><FlaskConical className="w-6 h-6 text-cobalt" /> End-to-End Test Control Center</h2>
          <p className="text-sm text-slate-500">Synthetic clients Tom Hanks, Amanda Hanks & Brenda Carpenter — real workflow endpoints, safe test payments, full rule logging.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => act("Seed", () => api.post("/admin/test-center/seed"))} disabled={!!busy} data-testid="tc-seed"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-navy text-white text-sm font-semibold disabled:opacity-50">
            <RefreshCw className="w-4 h-4" /> {data.seeded ? "Re-seed staging accounts" : "Seed staging accounts"}
          </button>
          <button onClick={() => { if (window.confirm("Delete ALL synthetic staging data? Production/demo data is untouched.")) act("Cleanup", () => api.post("/admin/test-center/cleanup")); }}
            disabled={!!busy} data-testid="tc-cleanup"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-600 text-white text-sm font-semibold disabled:opacity-50">
            <Trash2 className="w-4 h-4" /> Delete synthetic data
          </button>
        </div>
      </div>

      {data.seed_run && (
        <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center justify-between text-sm">
          <div>Seed: {badge(data.seed_run.status === "seeded" ? "PASS" : data.seed_run.status === "running" ? "running" : "FAIL")}
            <span className="ml-2 text-slate-400 text-xs">{data.seed_run.run_id} · {data.seed_run.started_at?.slice(0, 19)}</span></div>
          <button onClick={() => openLog(data.seed_run.run_id)} data-testid="tc-seed-log" className="text-xs font-semibold text-cobalt flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> View seed log</button>
        </div>
      )}

      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
            {["Client", "Scenario", "Case", "RN", "Retainer", "Appointment", "Chart", "Progress", "Result", "Actions"].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {data.clients.map((r) => (
              <tr key={r.client_key} className="border-b border-slate-50" data-testid={`tc-row-${r.client_key}`}>
                <td className="py-3 pr-3 font-medium text-navy">{r.client_name}</td>
                <td className="py-3 pr-3 text-xs text-slate-500">{r.scenario}</td>
                <td className="py-3 pr-3 text-xs">{r.case_number || r.case_id || "—"}<div className="text-slate-400">{r.case_status}{r.attorney_assigned ? " · attorney ✓" : ""}</div></td>
                <td className="py-3 pr-3 text-xs">{r.rn_status || "—"}</td>
                <td className="py-3 pr-3 text-xs">{r.retainer_status || "—"}</td>
                <td className="py-3 pr-3 text-xs">{r.appointment_status || "—"}</td>
                <td className="py-3 pr-3 text-xs">{r.chart_status || "—"}</td>
                <td className="py-3 pr-3">
                  {r.completion_pct != null ? (
                    <div className="w-24"><div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-cobalt" style={{ width: `${r.completion_pct}%` }} /></div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{r.completion_pct}% · {r.fail_count} fail(s)</div></div>
                  ) : "—"}
                </td>
                <td className="py-3 pr-3">{badge(r.run_status === "running" ? "running" : r.pass_fail)}</td>
                <td className="py-3 pr-3">
                  <div className="flex gap-1.5">
                    <button onClick={() => act(`Run ${r.client_name}`, () => api.post(`/admin/test-center/run/${r.client_key}`))}
                      disabled={!!busy || !data.seeded || r.run_status === "running"} data-testid={`tc-run-${r.client_key}`}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-cobalt text-white text-xs font-semibold disabled:opacity-40">
                      <Play className="w-3 h-3" /> {r.run_id ? "Rerun" : "Run"}
                    </button>
                    {r.run_id && (
                      <button onClick={() => openLog(r.run_id)} data-testid={`tc-log-${r.client_key}`}
                        className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-semibold">Log</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TestDriveControls />

      {log && (
        <div className="fixed inset-0 z-[60] bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLog(null)}>
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="tc-log-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-navy">{log.client_name || "Seed"} — workflow event & rule-evaluation log</h3>
              <button onClick={() => setLog(null)} data-testid="tc-log-close" className="p-1.5 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-slate-400 mb-3">{log.run_id} · {log.status} · {badge(log.pass_fail)} · {log.steps?.length} steps</div>
            <div className="space-y-1.5">
              {(log.steps || []).map((s) => (
                <div key={s.n} className={`rounded-xl border px-3 py-2 ${s.ok ? "border-slate-100" : "border-red-200 bg-red-50/50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-navy">{s.n}. {s.name}</div>
                    <div className="text-[10px] text-slate-400 whitespace-nowrap">{s.ok ? "✓" : "✗"} {s.status_code || ""} · {s.at?.slice(11, 19)}</div>
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">{s.method} {s.path || ""} <span className="text-slate-400">({s.role})</span></div>
                  {s.rule && <div className="text-[11px] text-cobalt mt-0.5">Rule: {s.rule}</div>}
                  {s.detail && <div className="text-[11px] text-slate-400 mt-0.5 break-all">{s.detail}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
